import { FC, MutableRefObject, ReactNode } from 'react';
import { COLOURS } from '@belong/themes';
import { ISectionWrapper, OUTER_SPACING } from '@belong/types';
import { SECTION_WRAPPER_TEST_ID } from '../../../helpers/testIds';
import { IContainer } from '../../layout/Container';
import { Stack } from '../../ui/Stack/Stack';
import { Anchor, SectionWrapper } from './Section.styles';
import SectionContainer from './SectionContainer';

export interface ISection extends IContainer, ISectionWrapper {
  className?: string;
  children: ReactNode;
  'data-testid'?: string;
  'data-contentid'?: string;
  'data-identifier'?: string;
  sectionRef?: MutableRefObject<HTMLDivElement | null>;
  hasContainer?: boolean;
  hasMargin?: boolean;
  [key: string]: unknown;
}

export const backgroundMap: { [k: string]: string } = {
  transparent: 'transparent',
  white: COLOURS.WHITE,
  grey: COLOURS.GREY_100,
  neutralDark: COLOURS.GREY_700,
  black: COLOURS.BLACK
};

const Section: FC<ISection> = ({
  hasBackground,
  hasPaddingTop = false,
  hasPaddingBottom = false,
  hasMarginTop = false,
  hasContainer = true,
  isPadded,
  isFullWidth,
  id,
  className,
  children,
  containsCard,
  sectionRef,
  paddingSize = OUTER_SPACING.LOOSE,
  ...others
}: ISection) => {
  const wrapperProps = {
    className,
    hasPaddingTop,
    hasPaddingBottom,
    hasBackground: hasBackground ? backgroundMap[hasBackground] : 'default',
    hasMarginTop,
    paddingSize,
    'data-state': others['data-state']
  };
  const dataIdentifier = others['data-identifier'];
  const anchorId = id || (dataIdentifier ? `${dataIdentifier}-anchor` : undefined);
  const hasAnchor = !!anchorId;

  return (
    <SectionWrapper
      {...wrapperProps}
      containsCard={containsCard}
      data-testid={others['data-testid'] || SECTION_WRAPPER_TEST_ID.ROOT}
      data-identifier={dataIdentifier}
      data-contentid={others['data-contentid']}
      id={dataIdentifier}
      ref={sectionRef}
      {...others}
    >
      {hasAnchor && <Anchor id={anchorId} data-testid={SECTION_WRAPPER_TEST_ID.ANCHOR} />}
      {/* todo: Section should not wrap its children in Stack. It should be done by the consumer of this component */}
      <SectionContainer hasContainer={hasContainer} isPadded={isPadded} isFullWidth={isFullWidth}>
        <Stack data-testid={SECTION_WRAPPER_TEST_ID.INNER_SECTION}>{children}</Stack>
      </SectionContainer>
    </SectionWrapper>
  );
};

Section.displayName = 'Section';
export default Section;
